<template>
  <div 
    v-if="visible" 
    class="advanced-image-preview"
    @click="handleBackdropClick"
    @wheel="handleWheel"
    tabindex="0"
    ref="container">
    
    <!-- 工具栏 -->
    <div class="preview-toolbar">
      <div class="toolbar-left">
        <span class="image-info">{{ currentIndex + 1 }} / {{ imageList.length }}</span>
      </div>
      <div class="toolbar-right">
        <button @click="zoomOut" class="toolbar-btn" title="缩小">
          <i class="el-icon-zoom-out"></i>
        </button>
        <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
        <button @click="zoomIn" class="toolbar-btn" title="放大">
          <i class="el-icon-zoom-in"></i>
        </button>
        <button @click="resetZoom" class="toolbar-btn" title="重置">
          <i class="el-icon-refresh"></i>
        </button>
        <button @click="close" class="toolbar-btn close-btn" title="关闭">
          <i class="el-icon-close"></i>
        </button>
      </div>
    </div>

    <!-- 图片容器 -->
    <div class="image-container" ref="imageContainer" @click="handleBackdropClick">
      <img 
        ref="image"
        :src="currentImage"
        :style="imageStyle"
        @mousedown="handleMouseDown"
        @load="handleImageLoad"
        @error="handleImageError"
        @click.stop
        draggable="false"
        class="preview-image" />
    </div>

    <!-- 导航按钮 -->
    <div v-if="imageList.length > 1" class="navigation">
      <button 
        @click="prevImage" 
        :disabled="currentIndex === 0"
        class="nav-btn prev-btn"
        title="上一张 (←)">
        <i class="el-icon-arrow-left"></i>
      </button>
      <button 
        @click="nextImage" 
        :disabled="currentIndex === imageList.length - 1"
        class="nav-btn next-btn"
        title="下一张 (→)">
        <i class="el-icon-arrow-right"></i>
      </button>
    </div>

    <!-- 缩略图导航 -->
    <div v-if="imageList.length > 1" class="thumbnail-nav">
      <div 
        v-for="(img, index) in imageList" 
        :key="index"
        @click="goToImage(index)"
        :class="['thumbnail-item', { active: index === currentIndex }]">
        <img :src="buildOssImgUrl(img, 60)" :alt="`缩略图 ${index + 1}`" />
      </div>
    </div>
  </div>
</template>

<script>
import { buildOssImgUrl } from '@/utils/oss-img';

export default {
  name: 'AdvancedImagePreview',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    imageList: {
      type: Array,
      default: () => []
    },
    initialIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      currentIndex: 0,
      scale: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      initialTranslateX: 0,
      initialTranslateY: 0,
      imageLoaded: false,
      imageError: false
    }
  },
  computed: {
    currentImage() {
      return this.imageList[this.currentIndex] || ''
    },
    imageStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
        cursor: this.isDragging ? 'grabbing' : (this.scale > 1 ? 'grab' : 'default'),
        transition: this.isDragging ? 'none' : 'transform 0.3s ease'
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.currentIndex = this.initialIndex
        this.resetZoom()
        this.$nextTick(() => {
          // 确保容器能够接收键盘事件
          this.$refs.container?.focus()
        })
        // 添加全局事件监听
        document.addEventListener('mousemove', this.handleMouseMove)
        document.addEventListener('mouseup', this.handleMouseUp)
        document.addEventListener('keydown', this.handleGlobalKeydown)
        document.body.style.overflow = 'hidden'
      } else {
        // 移除全局事件监听
        document.removeEventListener('mousemove', this.handleMouseMove)
        document.removeEventListener('mouseup', this.handleMouseUp)
        document.removeEventListener('keydown', this.handleGlobalKeydown)
        document.body.style.overflow = ''
      }
    },
    currentIndex() {
      this.resetZoom()
      this.imageLoaded = false
      this.imageError = false
    }
  },
  methods: {
    buildOssImgUrl,
    
    close() {
      this.$emit('close')
    },
    
    // 处理点击遮罩关闭
    handleBackdropClick(e) {
      // 只有点击遮罩背景时才关闭，点击图片不关闭
      if (e.target === e.currentTarget) {
        this.close()
      }
    },
    
    // 全局键盘事件处理（确保在任何情况下都能响应）
    handleGlobalKeydown(e) {
      this.handleKeydown(e)
    },
    
    // 键盘事件处理
    handleKeydown(e) {
      // 阻止默认行为，避免页面滚动等
      e.preventDefault()
      
      switch (e.key) {
        case 'Escape':
          this.close()
          break
        case 'ArrowLeft':
        case 'Left': // 兼容旧版浏览器
          this.prevImage()
          break
        case 'ArrowRight':
        case 'Right': // 兼容旧版浏览器
          this.nextImage()
          break
        case '+':
        case '=':
          this.zoomIn()
          break
        case '-':
          this.zoomOut()
          break
        case '0':
          this.resetZoom()
          break
        case 'Home':
          // 跳转到第一张
          if (this.imageList.length > 1) {
            this.currentIndex = 0
          }
          break
        case 'End':
          // 跳转到最后一张
          if (this.imageList.length > 1) {
            this.currentIndex = this.imageList.length - 1
          }
          break
      }
    },
    
    handleWheel(e) {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      this.zoom(delta, e.clientX, e.clientY)
    },
    
    zoomIn() {
      this.zoom(0.2)
    },
    
    zoomOut() {
      this.zoom(-0.2)
    },
    
    zoom(delta, centerX, centerY) {
      const newScale = Math.max(0.1, Math.min(5, this.scale + delta))
      
      if (centerX !== undefined && centerY !== undefined) {
        const rect = this.$refs.imageContainer.getBoundingClientRect()
        const offsetX = centerX - rect.left - rect.width / 2
        const offsetY = centerY - rect.top - rect.height / 2
        
        this.translateX = this.translateX - offsetX * (newScale / this.scale - 1)
        this.translateY = this.translateY - offsetY * (newScale / this.scale - 1)
      }
      
      this.scale = newScale
    },
    
    resetZoom() {
      this.scale = 1
      this.translateX = 0
      this.translateY = 0
    },
    
    handleMouseDown(e) {
      if (this.scale <= 1) return
      
      e.preventDefault()
      this.isDragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.initialTranslateX = this.translateX
      this.initialTranslateY = this.translateY
    },
    
    handleMouseMove(e) {
      if (!this.isDragging) return
      
      const deltaX = e.clientX - this.dragStartX
      const deltaY = e.clientY - this.dragStartY
      
      this.translateX = this.initialTranslateX + deltaX
      this.translateY = this.initialTranslateY + deltaY
    },
    
    handleMouseUp() {
      this.isDragging = false
    },
    
    prevImage() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      } else if (this.imageList.length > 1) {
        // 循环到最后一张
        this.currentIndex = this.imageList.length - 1
      }
    },
    
    nextImage() {
      if (this.currentIndex < this.imageList.length - 1) {
        this.currentIndex++
      } else if (this.imageList.length > 1) {
        // 循环到第一张
        this.currentIndex = 0
      }
    },
    
    goToImage(index) {
      if (index >= 0 && index < this.imageList.length) {
        this.currentIndex = index
      }
    },
    
    handleImageLoad() {
      this.imageLoaded = true
      this.imageError = false
    },
    
    handleImageError() {
      this.imageError = true
      this.imageLoaded = false
    }
  },
  
  beforeDestroy() {
    // 清理事件监听
    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mouseup', this.handleMouseUp)
    document.removeEventListener('keydown', this.handleGlobalKeydown)
    document.body.style.overflow = ''
  }
}
</script>

<style lang="scss" scoped>
.advanced-image-preview {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.75); /* 调整遮罩透明度更高 */
  z-index: 9999;
  display: flex;
  flex-direction: column;
  outline: none;
  
  /* 确保点击事件正确处理 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
  }
}

.preview-toolbar {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10000;
  pointer-events: none; /* 让工具栏不阻挡点击事件 */
  
  .toolbar-left,
  .toolbar-right {
    pointer-events: auto; /* 恢复子元素的点击事件 */
  }
  
  .toolbar-left {
    .image-info {
      color: white;
      font-size: 14px;
      background: rgba(0, 0, 0, 0.7);
      padding: 8px 12px;
      border-radius: 4px;
      backdrop-filter: blur(4px);
    }
  }
  
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .zoom-info {
      color: white;
      font-size: 14px;
      background: rgba(0, 0, 0, 0.7);
      padding: 8px 12px;
      border-radius: 4px;
      min-width: 50px;
      text-align: center;
      backdrop-filter: blur(4px);
    }
  }
  
  .toolbar-btn {
    background: rgba(0, 0, 0, 0.7);
    border: none;
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    backdrop-filter: blur(4px);
    
    &:hover {
      background: rgba(0, 0, 0, 0.9);
      transform: scale(1.05);
    }
    
    &.close-btn:hover {
      background: rgba(255, 0, 0, 0.7);
    }
    
    i {
      font-size: 16px;
    }
  }
}

.image-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  .preview-image {
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
    user-select: none;
    transform-origin: center;
    
    /* 添加加载状态样式 */
    &[src=""] {
      opacity: 0;
    }
  }
}

.navigation {
  pointer-events: none; /* 让导航容器不阻挡点击事件 */
  
  .nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.7);
    border: none;
    color: white;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    z-index: 10000;
    backdrop-filter: blur(4px);
    pointer-events: auto; /* 恢复按钮的点击事件 */
    
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.9);
      transform: translateY(-50%) scale(1.1);
    }
    
    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
    
    &.prev-btn {
      left: 20px;
    }
    
    &.next-btn {
      right: 20px;
    }
    
    i {
      font-size: 20px;
    }
  }
}

.thumbnail-nav {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px;
  border-radius: 8px;
  max-width: 80vw;
  overflow-x: auto;
  backdrop-filter: blur(4px);
  pointer-events: auto; /* 缩略图导航需要保持可点击 */
  
  .thumbnail-item {
    width: 60px;
    height: 60px;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.2s;
    flex-shrink: 0;
    
    &.active {
      border-color: #409eff;
      box-shadow: 0 0 10px rgba(64, 158, 255, 0.5);
    }
    
    &:hover {
      transform: scale(1.1);
      border-color: rgba(255, 255, 255, 0.5);
    }
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

/* 滚动条样式优化 */
.thumbnail-nav::-webkit-scrollbar {
  height: 4px;
}

.thumbnail-nav::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}

.thumbnail-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.thumbnail-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .preview-toolbar {
    top: 10px;
    left: 10px;
    right: 10px;
    
    .toolbar-left .image-info,
    .toolbar-right .zoom-info {
      font-size: 12px;
      padding: 6px 8px;
    }
    
    .toolbar-btn {
      width: 36px;
      height: 36px;
      
      i {
        font-size: 14px;
      }
    }
  }
  
  .navigation .nav-btn {
    width: 40px;
    height: 40px;
    left: 10px;
    
    &.next-btn {
      right: 10px;
    }
    
    i {
      font-size: 16px;
    }
  }
  
  .thumbnail-nav {
    bottom: 10px;
    max-width: 90vw;
    
    .thumbnail-item {
      width: 50px;
      height: 50px;
    }
  }
}
</style>